import React from 'react';
import { motion } from 'framer-motion';
import { FaExternalLinkAlt, FaStar } from 'react-icons/fa';

// In-house products built and operated by PlatypusBox.
// Logos/cards are fetched from each product's own site (see /public/products).
const products = [
  {
    name: 'PlatypusAgent',
    url: 'https://agent.platypusbox.com',
    host: 'agent.platypusbox.com',
    tagline: 'Let your imagination move out of the box',
    description:
      'A private AI coding agent that runs on your own PC. Your code stays with you. Request access and get your license.',
    tags: ['AI Coding Agent', 'Runs Locally', 'Private by Design'],
    cta: 'Get PlatypusAgent',
    icon: '/products/platypusagent-icon.png',
    card: '/products/platypusagent-card.png',
    cardBg: 'bg-[#13331f]',
    accent: '#C4694A',
    glow: 'from-[#C4694A]/25',
  },
  {
    name: 'Helpaa',
    url: 'https://www.helpaa.com',
    host: 'helpaa.com',
    tagline: 'Help at your doorstep',
    description:
      'On-demand local services. Connects you with verified local professionals, booked in minutes and tracked in real time.',
    tags: ['On-Demand Services', 'Verified Pros', 'Live Tracking'],
    cta: 'Visit Helpaa',
    icon: '/products/helpaa-icon.png',
    card: '/products/helpaa-card.png',
    cardBg: 'bg-white',
    accent: '#FF5A00',
    glow: 'from-[#FF5A00]/25',
  },
];

const InHouseProducts = () => {
  return (
    <section
      id="products"
      className="relative bg-[#061a10] py-24 px-4 overflow-hidden"
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[60rem] h-[30rem] bg-accent-green/10 rounded-full blur-[160px] pointer-events-none" />

      <div className="relative z-10 container mx-auto max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <span className="inline-flex items-center gap-2 bg-accent-green/15 border border-accent-green/40 text-accent-green tracking-[0.25em] text-xs font-semibold uppercase px-4 py-1.5 rounded-full mb-5">
            <FaStar className="text-[10px]" /> Just Launched
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-cream mb-5">
            Our <span className="text-accent-green">In-House</span> Products
          </h2>
          <p className="text-cream/80 text-lg md:text-xl max-w-3xl mx-auto">
            Beyond client work, we build and run our own products. Meet the two
            newest members of the PlatypusBox family.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {products.map((p, index) => (
            <motion.a
              key={p.name}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.15 }}
              whileHover={{ y: -6 }}
              className="group relative flex flex-col bg-forest/70 border border-accent-green/20 hover:border-accent-green/60 rounded-xl overflow-hidden shadow-xl shadow-black/30 transition-colors"
            >
              <span
                className="absolute top-4 right-4 z-10 text-[11px] font-bold tracking-widest uppercase px-3 py-1 rounded-full text-white shadow-lg"
                style={{ backgroundColor: p.accent }}
              >
                New
              </span>

              <div className={`${p.cardBg} aspect-[1200/630] overflow-hidden`}>
                <img
                  src={p.card}
                  alt={`${p.name} logo`}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>

              <div className={`h-1 bg-gradient-to-r ${p.glow} to-transparent`} style={{ backgroundColor: p.accent }} />

              <div className="p-7 flex flex-col flex-1">
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src={p.icon}
                    alt=""
                    className="w-14 h-14 rounded-xl shadow-md"
                    loading="lazy"
                  />
                  <div>
                    <h3 className="text-2xl font-bold text-cream">{p.name}</h3>
                    <p className="text-sm italic" style={{ color: p.accent }}>
                      {p.tagline}
                    </p>
                  </div>
                </div>

                <p className="text-cream/75 mb-5 flex-1">{p.description}</p>

                <div className="flex flex-wrap gap-2 mb-6">
                  {p.tags.map((t) => (
                    <span
                      key={t}
                      className="text-xs text-cream/80 border border-cream/20 rounded-full px-3 py-1"
                    >
                      {t}
                    </span>
                  ))}
                </div>

                <span className="inline-flex items-center justify-between gap-3 bg-accent-green text-forest font-semibold px-5 py-3 rounded-md group-hover:bg-accent-green/90 transition-colors">
                  <span>{p.cta}</span>
                  <span className="inline-flex items-center gap-2 text-sm font-normal opacity-80">
                    {p.host} <FaExternalLinkAlt className="text-xs" />
                  </span>
                </span>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};

export default InHouseProducts;
